import { FC, useCallback, useMemo, useState } from 'react';
import {
  Button,
  Col,
  List,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Space,
  Switch,
  Typography
} from 'antd';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { useMutation } from '@apollo/client';
import { LoadingOutlined } from '@ant-design/icons';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { AppointmentById_appointment_data } from 'gql/appointments/__generated__/AppointmentById';
import { APPOINTMENT_ITEM_FRAGMENT } from 'gql/appointments/fragments';
import { useTranslation } from 'react-i18next';
import { TRANSLATE } from 'gql/appointments/mutation';
import client from 'apolloClient';
import { APPOINTMENT_CREATOR } from 'constants/appointments';

import { StyledLoading } from './styled';
import { replaceNotesWithArrayValues } from 'utils/translation';

const { Title, Text, Link } = Typography;

const options = [
  { label: 'English', value: 'en' },
  { label: 'German', value: 'de' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Russian', value: 'ru' }
];

const TranslationModal: FC = () => {
  // translations
  const { t, i18n } = useTranslation();
  // states
  const [showLanguages, setShowLanguages] = useState(false);
  const [language, setLanguage] = useState(i18n.language);
  const [translate, setTranslate] = useState(false);
  // context
  const { translation } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();

  const data = translation.params?.data;
  const appointment_id = translation.params?.appointment_id;

  const notes = useMemo(
    () =>
      data?.filter(item => item?.sender === APPOINTMENT_CREATOR.patient) || [],
    [data]
  );

  const selectedLang = useMemo(
    () => options.find(e => language === e.value)?.label,
    [language]
  );

  // graphql
  const [translateText, { loading }] = useMutation(TRANSLATE, {
    onCompleted() {
      toggleModal('translation', false);
    },
    update(cache, result) {
      const appointment: AppointmentById_appointment_data | null =
        cache.readFragment({
          id: `Appointment:${appointment_id}`,
          fragment: APPOINTMENT_ITEM_FRAGMENT,
          fragmentName: 'AppointmentItem'
        });

      const patientNotes = replaceNotesWithArrayValues(
        notes,
        result?.data?.translate?.data.body
      );

      const translatedNotes = notes?.map(item => {
        return {
          ...item,
          note:
            item.sender === APPOINTMENT_CREATOR.patient
              ? patientNotes.find(x => x.id === item.id)?.note
              : item.note
        };
      });

      if (appointment) {
        cache.writeFragment({
          id: `AppointmentNote:${appointment_id}`,
          fragment: APPOINTMENT_ITEM_FRAGMENT,
          fragmentName: 'AppointmentItem',
          data: {
            ...appointment,
            notes: translatedNotes
          }
        });
      }
    }
  });

  const onOk = useCallback(() => {
    const notesData = notes?.map(note => note.note);

    try {
      if (translate) {
        translateText({
          variables: {
            input: {
              body: notesData,
              lang: language
            }
          }
        });
      } else {
        client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
        toggleModal('translation', false);
      }
    } catch (err) {
      console.warn('err', err);
    }
  }, [notes, language, toggleModal, translate, translateText]);

  const closeLanguages = () => {
    setShowLanguages(false);
  };

  const onChangeLang = ({ target: { value } }: RadioChangeEvent) => {
    setLanguage(value);
    setShowLanguages(false);
  };

  return (
    <Modal
      title={t('appointments_details.translation')}
      open={translation?.visible}
      closable={false}
      footer={[
        <Button
          key="ok"
          type="default"
          onClick={showLanguages ? closeLanguages : onOk}
        >
          {showLanguages ? t('common.cancel') : 'OK'}
        </Button>
      ]}
    >
      {loading ? (
        <StyledLoading>
          <LoadingOutlined style={{ fontSize: 50 }} />
        </StyledLoading>
      ) : showLanguages ? (
        <Radio.Group
          value={language}
          onChange={onChangeLang}
          style={{ width: '100%' }}
        >
          <List
            itemLayout="horizontal"
            dataSource={options}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta title={t(`common:${item.label}`)} />
                <div>
                  <Radio value={item.value} />
                </div>
              </List.Item>
            )}
          />
        </Radio.Group>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row justify="space-between" align="top">
            <Col>
              <Title level={5} style={{ color: 'rgba(45, 44, 76, 0.85)' }}>
                {t('appointments_details.translate_conversation')}
              </Title>
              <Text type="secondary">{selectedLang}</Text>
            </Col>
            <Col>
              <Switch checked={translate} onChange={val => setTranslate(val)} />
            </Col>
          </Row>
          <Space direction="vertical" size={0}>
            <Text>{t('appointments_details.change_language')}</Text>
            <Link onClick={() => setShowLanguages(true)}>
              {t('appointments_details.translation_settings')}
            </Link>
          </Space>
        </Space>
      )}
    </Modal>
  );
};

export default TranslationModal;
